import { useEffect, useState } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import PanelActions from '../../page-framework/panel/PanelActions';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { getExtendedMetricColor, usePaletteType } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { interfaceFilterId, timeRangeFilterId, useInterface } from '../../filters';
import { useCurrentFilterStateById } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`CBQoS Discards`;

const RATE = 'RATE';
const COUNT = 'COUNT';
const PERCENT = 'PERCENT';

const MAX_SERIES = 20;

const NO_CBQOS_CLASSES = [{ name: 'N/A', metric: '__nodata__' }];
const EMPTY_DATA = { trends: {} };

const typeConfig = {
    [RATE]: {
        formatter: FORMATTER_TYPES.LOG,
        yAxisTitle: t`Discards/min`,
        units: ` ${t`Discards/min`}`,
        metricUnits: 'Rate',
    },
    [COUNT]: {
        formatter: FORMATTER_TYPES.LOG,
        yAxisTitle: t`Discards`,
        units: ` ${t`Discards`}`,
        metricUnits: 'Count',
    },
    [PERCENT]: {
        formatter: FORMATTER_TYPES.PERCENT,
        forceDecimals: true,
        yAxisTitle: t`Discards (%)`,
        units: '%',
        metricUnits: 'Percent',
    },
};

/**
 * Build a CBQoS class name, including (In) or (Out) if both directions are defined for the same class.
 * @param {[Object]} qosClasses
 * @param {Object} qosClass
 * @returns {String}
 */
function buildCbQosClassName(qosClasses, qosClass) {
    const count = qosClasses.reduce((sum, record) => (record.name === qosClass.name ? sum + 1 : sum), 0);
    return count < 2 ? qosClass.name : `${qosClass.name} (${qosClass.direction === 1 ? 'In' : 'Out'})`;
}

/**
 * Build the metric name used for Cassandra metrics based on the metric basename, the metric type, the direction,
 * and whether or not it's a status metric.
 * @param {Object} qosClass
 * @param {String} metricUnits
 * @param {Boolean} statusMetric
 * @returns {String}
 */
function buildCbQosMetricName(qosClass, metricUnits, statusMetric) {
    const direction = qosClass.direction === 1 ? 'In' : 'Out';
    if (statusMetric) {
        return `statusCbQosDiscard${direction}_${qosClass.metric}`;
    }
    return `cbQosDiscard${metricUnits}${direction}_${qosClass.metric}`;
}

/**
 * Glue the various sets of data together (cbQosClasses, interface, and metrics)
 * @param cbQosData
 * @param interfaceData
 * @param metricsData
 * @returns {*}
 */
function buildChartData(cbQosData, interfaceData, metricsData) {
    interfaceData.cbQosDefs = cbQosData;
    interfaceData.trends = metricsData.trends;
    return interfaceData;
}

/**
 * Build the config for the trends to plot, one per CBQoS class. This routine expect that the qosClasses
 * list has been sorted and trimmed appropriately.
 * @param {String} type
 * @param {[Object]} qosClasses
 * @param {String} palette
 * @returns {[Object]}
 */
function buildChartTrends(type, qosClasses, palette) {
    const config = typeConfig[type];
    return qosClasses.map((qosClass, index) => {
        return {
            formatter: config.formatter,
            units: config.units,
            chartType: 'spline',
            key: buildCbQosMetricName(qosClass, config.metricUnits, false),
            statusKey: buildCbQosMetricName(qosClass, config.metricUnits, true),
            label: buildCbQosClassName(qosClasses, qosClass),
            color: getExtendedMetricColor(index, palette),
            multiplier: 1,
            forceDecimals: config.forceDecimals === true,
        };
    });
}

/**
 * Build the set of parameters for a /query/chart request on the count metrics for this
 * panel, with both the CBQoS metrics and associated status metrics this interface has.
 * @param type
 * @param uuid
 * @param start
 * @param end
 * @param data
 * @returns {Object}
 */
function getChartRequestParams(type, uuid, start, end, data) {
    let qosMetrics = [];
    const units = typeConfig[type].metricUnits;
    if (data?.cbQosClasses?.length) {
        qosMetrics = _.flatMap(
            data.cbQosClasses.map((qosClass) => {
                return [buildCbQosMetricName(qosClass, units, false), buildCbQosMetricName(qosClass, units, true)];
            })
        );
    }
    return {
        type: 'interface',
        interface: uuid,
        start,
        end,
        metrics: [...qosMetrics, 'statusInterfaceCbQosDiscards'],
    };
}

/**
 * Check to see if the specified class name/direction has monitoring enabled.
 * @param qosClass
 * @param cbQosClasses
 * @returns {boolean}
 */
function isEnabledClass(qosClass, cbQosClasses) {
    return (
        _.findIndex(cbQosClasses, { metric: qosClass.metric, direction: qosClass.direction, enableMonitoring: true }) >=
        0
    );
}

/**
 * Process the CBQoS class data: 1) filter out disabled classes, 2) build a proper title for each,
 * 3) sort the data by those titles (case-insensitive), and 4) trim it to the first 20.
 * @param result
 * @returns {*}
 */
function processCbQosClasses(result) {
    const [cbQosData, interfaceData] = result;

    if (interfaceData?.cbQosClasses?.length) {
        const filteredQosClasses = interfaceData.cbQosClasses.filter((qosClass) => isEnabledClass(qosClass, cbQosData));
        _.forEach(filteredQosClasses, (qosClass) => {
            qosClass.title = buildCbQosClassName(cbQosData, qosClass);
        });
        interfaceData.cbQosClasses = _.orderBy(filteredQosClasses, [(prop) => prop.title.toLowerCase()], ['asc']).slice(
            0,
            MAX_SERIES
        );
    }

    return result;
}

/**
 * Build the chart config from the chart type (Rate/Count/%) and the set of enabled CBQoS classes
 * for this interface.
 * @param type
 * @param qosClasses
 * @param palette
 * @returns {Object}
 */
function buildChartConfig(type, qosClasses, palette) {
    if (!qosClasses) return null;
    return {
        yAxis: [
            {
                title: {
                    text: typeConfig[type].yAxisTitle,
                },
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusInterfaceCbQosDiscards',
        chartTrends: buildChartTrends(type, qosClasses, palette),
    };
}

/**
 * Query and post-process the data for the CBQoS chart, gluing data together and setting any status colors
 * needed for displaying the tooltip.
 * @param type
 * @returns {*[]}
 */
function useChartProps(type) {
    const intf = useInterface();
    const { uuid, hasCbQosClasses } = intf || {};
    const dataService = useDataService();
    const palette = usePaletteType();
    const [props, setProps] = useState();
    const [timeRangeFilterState] = useCurrentFilterStateById(timeRangeFilterId);
    const { start, end } = timeRangeFilterState || {};
    const startTime = start ? start.getTime() : null;
    const endTime = end ? end.getTime() : null;

    useEffect(() => {
        if (uuid && startTime) {
            if (!hasCbQosClasses) {
                // This can occur on a dashboard panel - set up an empty chart
                setProps({
                    data: EMPTY_DATA,
                    config: buildChartConfig(type, NO_CBQOS_CLASSES, palette),
                });
                return;
            }
            const cbQosRequest = dataService.get('/ngp/v2/cbqos-classes').then((result) => result?.data);
            const interfaceRequest = dataService.get(`/ipm/v1/open/interfaces/${uuid}`);
            Promise.all([cbQosRequest, interfaceRequest])
                .then(processCbQosClasses)
                .then(([cbQosData, interfaceData]) => {
                    const chartRequest = dataService.get(
                        '/query/chart',
                        getChartRequestParams(type, uuid, startTime, endTime, interfaceData)
                    );
                    chartRequest
                        .then((result) => result?.data?.[0])
                        .then((chartData) => {
                            const data = buildChartData(cbQosData, interfaceData, chartData);
                            const config = buildChartConfig(type, data?.cbQosClasses, palette);
                            setProps({
                                data,
                                config,
                            });
                        });
                });
        }
    }, [type, dataService, startTime, endTime, uuid, hasCbQosClasses, palette]);

    return props;
}

const selections = [
    { value: RATE, label: t`Rate` },
    { value: COUNT, label: t`Count` },
    { value: PERCENT, label: '%' },
];

function InterfaceCbqosPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, COUNT);
    const chartProps = useChartProps(chartDisplayType);

    if (!chartProps?.data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartProps.config} data={chartProps.data} />
        </>
    );
}

InterfaceCbqosPanel.propTypes = panelPropTypes;

InterfaceCbqosPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'CbqosDiscards',
    catalogEntry: {
        category: PANEL_CATEGORIES.INTERFACE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`CBQoS (Class Based Quality of Service) discards trend for a selected interface`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, interfaceFilterId],
        optional: [],
    },
})(InterfaceCbqosPanel);
